import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { InsightsV2 } from 'libs/end-to-end/src/insights';

export const MIN_TOP_OPPORTUNITY_CONFIDENCE_PERCENTAGE = 90;

export interface TopOpportunity {
  referenceLevelDefinition: LevelDefinition;
  treatmentLevelDefinition: LevelDefinition;
  metricDefinition: MyMetricDefinition;
  focusAreaDefinition: MyFocusAreaDefinition;
  rawDisparityPercentage: number;
  controlledDisparityPercentage: number;
  debiasedDisparityPercentage: number;
  confidencePercentage: number;
}

export const topOpportunityHasBias = (
  opportunity: Pick<
    TopOpportunity,
    'debiasedDisparityPercentage' | 'controlledDisparityPercentage'
  >,
): boolean => {
  return (
    opportunity.controlledDisparityPercentage !==
    opportunity.debiasedDisparityPercentage
  );
};

// eslint-disable-next-line complexity
export const sortTopThreeOpportunities = ({
  insights,
  focusAreaDefinitions,
  allLevels,
  showMetricsWithoutBiasTestTasks,
}: {
  insights: InsightsV2;
  focusAreaDefinitions: MyFocusAreaDefinition[];
  allLevels: LevelDefinition[];
  showMetricsWithoutBiasTestTasks: boolean;
}): TopOpportunity[] => {
  const opportunities: TopOpportunity[] = [];
  for (const insight of insights) {
    const focusAreaDefinition = focusAreaDefinitions.find(
      (f) => f.focusArea === insight.focusArea,
    );

    if (!focusAreaDefinition) {
      continue;
    }

    const metricDefinition = focusAreaDefinition.metrics.find(
      (m) => m.columnHeaderValue === insight.metric,
    );

    if (!metricDefinition) {
      return [];
    }

    const referenceLevelDefinition = allLevels.find(
      (level) =>
        level.columnValue === insight.referenceLevel && level.isPrivileged,
    );

    const treatmentLevelDefinition = allLevels.find(
      (level) => level.columnValue === insight.treatmentLevel,
    );

    const debiasedDisparityPercentage =
      insight.debiasedDisparity === null
        ? insight.controlledDisparity
        : insight.debiasedDisparity;

    if (
      !showMetricsWithoutBiasTestTasks &&
      !metricDefinition.hasBiasTestTasks
    ) {
      continue;
    }

    if (
      !referenceLevelDefinition ||
      !treatmentLevelDefinition ||
      insight.rawDisparity === null ||
      insight.controlledDisparity === null ||
      insight.confidence === null ||
      debiasedDisparityPercentage === null ||
      debiasedDisparityPercentage === 0
    ) {
      continue;
    }

    opportunities.push({
      referenceLevelDefinition,
      treatmentLevelDefinition,
      metricDefinition,
      focusAreaDefinition,
      confidencePercentage: 100 - insight.confidence,
      rawDisparityPercentage: insight.rawDisparity,
      controlledDisparityPercentage: insight.controlledDisparity,
      debiasedDisparityPercentage,
    });
  }

  const filteredOpportunities = opportunities.filter((opportunity) => {
    return (
      topOpportunityHasBias(opportunity) ||
      opportunity.confidencePercentage >=
        MIN_TOP_OPPORTUNITY_CONFIDENCE_PERCENTAGE
    );
  });

  // This sorts the opportunities by the absolute difference between the debiased disparity and the controlled disparity with the largest difference first
  const sortedOpportunities = filteredOpportunities.sort((a, b) => {
    const impactA = Math.abs(
      a.debiasedDisparityPercentage - a.controlledDisparityPercentage,
    );
    const impactB = Math.abs(
      b.debiasedDisparityPercentage - b.controlledDisparityPercentage,
    );

    return impactB - impactA;
  });

  return sortedOpportunities.slice(0, 3);
};
